import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Modal } from "../ui/modal";
import { Button } from "../ui/button";
import { Field, Input, Select, Textarea } from "../ui/input";
import { useToastStore } from "../../stores/toast";

// Création d'un établissement en une seule fenêtre : seul le NOM est requis,
// tout le reste a un défaut sensé et peut être réglé plus tard dans la page
// Configuration. Les sections rarement remplies (téléphonie, avancé) sont
// repliées — même grammaire que « Nouveau dossier » du CRM.

const FORM_ID = "tenant-create-form";

const TYPES = [
  { value: "restaurant", label: "Restaurant" },
  { value: "garage", label: "Garage" },
  { value: "velo", label: "Atelier vélo" },
  { value: "salon", label: "Salon / Coiffeur / Bien-être" },
  { value: "consulting", label: "Consultation / Agence B2B" },
  { value: "custom", label: "Autre / sur mesure" },
] as const;

type TenantType = (typeof TYPES)[number]["value"];

// Le type détermine les OUTILS de l'agent : les verticaux enregistrent de
// vraies réservations, `custom`/`consulting` prennent seulement des messages.
const TYPE_HINT: Record<TenantType, string> = {
  restaurant: "L'agent enregistre de vraies réservations de table.",
  garage: "L'agent enregistre de vrais rendez-vous d'atelier.",
  velo: "L'agent enregistre de vrais rendez-vous de service.",
  salon: "L'agent enregistre de vrais rendez-vous.",
  consulting: "Prise de message : l'agent note la demande, l'équipe rappelle.",
  custom: "Prise de message : l'agent note la demande, l'équipe rappelle.",
};

const LANGUAGES = [
  { value: "fr-CA", label: "Français (Québec)" },
  { value: "fr-FR", label: "Français (France)" },
  { value: "en-US", label: "Anglais (États-Unis)" },
];

const TIMEZONES = ["America/Montreal", "America/Toronto", "America/Vancouver", "Europe/Paris"];

// Défauts maison : Pascal QC + Turbo v2.5 pour le français (meilleure
// prononciation), Sarah pour l'anglais.
const DEFAULT_VOICE_FR = "nXbV1oNjVZJeyUXiHwTn";
const DEFAULT_VOICE_EN = "EXAVITQu4vr4xnSDxMaL";
const DEFAULT_VOICE_SETTINGS = "turbo_v2_5-1.0_0.6_0.85";

interface VoiceOption {
  voice_id: string;
  name: string;
  labels?: Record<string, string>;
}

export interface FreeNumber {
  sid: string;
  e164: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  freeNumbers: FreeNumber[];
  /** Appelé après création réussie (id du tenant) pour rafraîchir les listes. */
  onCreated: (tenantId: string) => void | Promise<void>;
}

function formatE164(e164: string): string {
  const m = e164.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  return m ? `+1 ${m[1]} ${m[2]} ${m[3]}` : e164;
}

/** Accepte « 819 555 1234 », « (819) 555-1234 », « +1 819 555 1234 ». */
function normalizePhone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const digits = trimmed.replace(/\D+/g, "");
  if (trimmed.startsWith("+") && digits.length >= 8) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
  return null;
}

export function TenantCreateModal({ open, onClose, freeNumbers, onCreated }: Props) {
  const push = useToastStore((s) => s.push);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [type, setType] = useState<TenantType>("restaurant");
  const [greeting, setGreeting] = useState("");
  const [language, setLanguage] = useState("fr-CA");
  const [timezone, setTimezone] = useState("America/Montreal");
  const [formality, setFormality] = useState<"vous" | "tu">("vous");
  const [voiceId, setVoiceId] = useState(DEFAULT_VOICE_FR);
  const [voiceTouched, setVoiceTouched] = useState(false);
  const [numberSid, setNumberSid] = useState("");
  const [transferTo, setTransferTo] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [nameError, setNameError] = useState<string | undefined>();
  const [transferError, setTransferError] = useState<string | undefined>();

  // Remise à zéro à chaque ouverture : une fenêtre rouverte ne doit pas garder
  // les restes d'une saisie abandonnée.
  useEffect(() => {
    if (!open) return;
    setName("");
    setType("restaurant");
    setGreeting("");
    setLanguage("fr-CA");
    setTimezone("America/Montreal");
    setFormality("vous");
    setVoiceId(DEFAULT_VOICE_FR);
    setVoiceTouched(false);
    setNumberSid("");
    setTransferTo("");
    setSystemPrompt("");
    setNameError(undefined);
    setTransferError(undefined);
  }, [open]);

  // Voix disponibles pour la langue choisie (preview natif, aucun coût TTS).
  useEffect(() => {
    if (!open) return;
    const lang = language.split("-")[0] ?? "fr";
    api<VoiceOption[]>(`/api/elevenlabs/voices?language=${encodeURIComponent(lang)}`)
      .then(setVoices)
      .catch(() => setVoices([]));
  }, [open, language]);

  // Changer de langue réaligne la voix par défaut — sauf si l'utilisateur a
  // déjà fait son choix lui-même.
  useEffect(() => {
    if (voiceTouched) return;
    setVoiceId(language.startsWith("en") ? DEFAULT_VOICE_EN : DEFAULT_VOICE_FR);
  }, [language, voiceTouched]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    if (!name.trim()) {
      setNameError("Le nom est requis.");
      return;
    }
    const transfer = transferTo.trim() ? normalizePhone(transferTo) : null;
    if (transferTo.trim() && !transfer) {
      setTransferError("Numéro invalide (ex : 819 555 1234).");
      return;
    }

    setSaving(true);
    try {
      const created = await api<{ id: string }>("/api/tenants", {
        method: "POST",
        body: JSON.stringify({
          name: name.trim(),
          type,
          language,
          timezone,
          formality,
          voiceId: voiceId || null,
          voiceSettings: DEFAULT_VOICE_SETTINGS,
          ...(greeting.trim() ? { greetingMessage: greeting.trim() } : {}),
          ...(systemPrompt.trim() ? { systemPrompt: systemPrompt.trim() } : {}),
        }),
      });

      // Étapes facultatives : un échec ici ne doit pas faire croire que la
      // création a échoué — l'établissement, lui, existe.
      const warnings: string[] = [];
      const chosen = freeNumbers.find((n) => n.sid === numberSid);
      if (chosen) {
        try {
          await api("/api/numbers/assign", {
            method: "POST",
            body: JSON.stringify({ e164: chosen.e164, twilioSid: chosen.sid, tenantId: created.id }),
          });
        } catch {
          warnings.push("le numéro n'a pas pu être connecté");
        }
      }
      if (transfer) {
        try {
          await api(`/api/tenants/${created.id}/forwards`, {
            method: "POST",
            body: JSON.stringify({ conditionIntent: "*", targetKind: "phone", targetValue: transfer }),
          });
        } catch {
          warnings.push("le transfert n'a pas pu être enregistré");
        }
      }

      if (warnings.length > 0) {
        push("warning", `Établissement créé, mais ${warnings.join(" et ")}.`);
      } else {
        push("success", `${name.trim()} créé${chosen ? ` — ${formatE164(chosen.e164)} connecté` : ""}`);
      }
      await onCreated(created.id);
      onClose();
    } catch {
      push("danger", "Échec de la création (réservé aux admins)");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Nouvel établissement"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" form={FORM_ID} variant="primary" disabled={saving || !name.trim()}>
            {saving ? "Création…" : "Créer l'établissement"}
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={submit}>
        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Identité</h3>
            <p className="form-section__hint">
              Seul le nom est requis — tout le reste se règle aussi après coup dans Configuration.
            </p>
          </div>

          <div className="form-section__grid form-section__grid--full">
            <Field label="Nom de l'établissement" error={nameError}>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(undefined);
                }}
                placeholder="ex : Bistro Laurier"
                autoFocus
              />
            </Field>
          </div>

          <div className="form-section__grid">
            <Field label="Métier" hint={TYPE_HINT[type]}>
              <Select value={type} onChange={(e) => setType(e.target.value as TenantType)}>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Registre" hint="« Vous » = professionnel (défaut).">
              <Select
                value={formality}
                onChange={(e) => setFormality(e.target.value as "vous" | "tu")}
              >
                <option value="vous">Vouvoiement</option>
                <option value="tu">Tutoiement</option>
              </Select>
            </Field>
          </div>

          <div className="form-section__grid form-section__grid--full">
            <Field label="Message d'accueil" hint="La première phrase entendue au décrochage.">
              <Textarea
                className="textarea--sm"
                rows={2}
                value={greeting}
                onChange={(e) => setGreeting(e.target.value)}
                placeholder="Bonjour, Bistro Laurier ! Comment puis-je vous aider ?"
              />
            </Field>
          </div>
        </section>

        <section className="form-section">
          <div className="form-section__head">
            <h3 className="form-section__title">Voix &amp; langue</h3>
            <p className="form-section__hint">
              Par défaut : Pascal QC en Turbo v2.5, notre meilleure diction française.
            </p>
          </div>

          <div className="form-section__grid">
            <Field label="Voix de l'agent">
              <Select
                value={voiceId}
                onChange={(e) => {
                  setVoiceId(e.target.value);
                  setVoiceTouched(true);
                }}
              >
                <option value="">Voix par défaut du système</option>
                {voices.map((v) => (
                  <option key={v.voice_id} value={v.voice_id}>
                    {v.name}
                    {v.labels?.accent ? ` — ${v.labels.accent}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Langue / accent">
              <Select value={language} onChange={(e) => setLanguage(e.target.value)}>
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Fuseau horaire">
              <Select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </section>

        <details className="form-section">
          <summary className="form-section__head">
            <Chevron />
            <span className="form-section__title">Téléphonie</span>
            <span className="form-section__count">
              — {freeNumbers.length > 0 ? `${freeNumbers.length} numéro${freeNumbers.length > 1 ? "s" : ""} libre${freeNumbers.length > 1 ? "s" : ""}` : "aucun numéro libre"}
            </span>
          </summary>

          <div className="form-section__grid">
            <Field
              label="Numéro à connecter"
              hint="Le webhook Twilio est configuré automatiquement."
            >
              <Select
                value={numberSid}
                onChange={(e) => setNumberSid(e.target.value)}
                disabled={freeNumbers.length === 0}
              >
                <option value="">Aucun pour l'instant</option>
                {freeNumbers.map((n) => (
                  <option key={n.sid} value={n.sid}>
                    {formatE164(n.e164)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label="Transfert vers un humain"
              hint="Sans ce numéro, les demandes deviennent des rappels."
              error={transferError}
            >
              <Input
                value={transferTo}
                onChange={(e) => {
                  setTransferTo(e.target.value);
                  if (transferError) setTransferError(undefined);
                }}
                placeholder="819 555 1234"
                inputMode="tel"
              />
            </Field>
          </div>
        </details>

        <details className="form-section">
          <summary className="form-section__head">
            <Chevron />
            <span className="form-section__title">Avancé</span>
            <span className="form-section__count">— prompt système</span>
          </summary>

          <div className="form-section__grid form-section__grid--full">
            <Field
              label="Prompt système"
              hint="Laisse vide pour le gabarit du métier choisi. Les règles de voix, de langue et de format sont ajoutées automatiquement."
            >
              <Textarea
                rows={6}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Tu es la réceptionniste virtuelle de…"
              />
            </Field>
          </div>
        </details>
      </form>
    </Modal>
  );
}

function Chevron() {
  return (
    <svg
      className="form-section__chevron"
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
